import { useEffect, useMemo, useState } from 'react';
import { proService } from '../services';
import { INK, MUTED, SUBTLE, INSET, RULE, PillButton } from './club/clubUi';
import { Card, CardHead, fieldLabel, RoundX } from './pro/setup/setupUi';

/**
 * The hours a Pro actually works, and the shape of a booking (design_handoff_pro_setup
 * "AVAILABILITY"): HOURS YOU WORK as seven day cards with as many windows as the day needs,
 * COPY TO WEEKDAYS / COPY TO ALL, then HOW LONG IS A BOOKING and ADVANCE NOTICE. SAVE HOURS
 * writes through at once (`PUT /pro/me/availability/hours`); the dates that break the pattern
 * are set on the SPECIFIC DATES calendar beside this and travel with the save untouched.
 *
 * Half-days couldn't say the thing that matters: an hour's lesson at 9am and a five-hour
 * reception starting at 4pm are different offers, and "PM" can't tell a client which one you take.
 */
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS = [1, 2, 3, 4, 5];

const toTime = (minutes) => {
  const m = Math.max(0, Math.min(1440, minutes || 0));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};
const toMinutes = (value) => {
  const [h, m] = String(value || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const DEFAULT_RANGE = { startMinute: 540, endMinute: 1020 }; // 9–5

/** Group flat windows from the API back into the shape this editor edits. */
export function fromWindows(windows = []) {
  const weekly = DAYS.map((_, i) => ({ dayOfWeek: i, windows: [] }));
  const exceptionsByDate = new Map();

  for (const w of windows) {
    if (w.date) {
      const entry = exceptionsByDate.get(w.date) || { date: w.date, closed: false, windows: [] };
      if (w.isClosed) entry.closed = true;
      else entry.windows.push({ startMinute: w.startMinute, endMinute: w.endMinute });
      exceptionsByDate.set(w.date, entry);
    } else if (w.dayOfWeek != null && weekly[w.dayOfWeek]) {
      weekly[w.dayOfWeek].windows.push({ startMinute: w.startMinute, endMinute: w.endMinute });
    }
  }
  return { weekly, exceptions: [...exceptionsByDate.values()].sort((a, b) => a.date.localeCompare(b.date)) };
}

const timePill = { flex: 1, minWidth: 0, height: 38, padding: '0 10px', border: 'none', borderRadius: 999, background: '#fff', fontFamily: 'inherit', fontSize: 12, color: INK, boxSizing: 'border-box', outline: 'none' };
const numberWrap = { display: 'flex', alignItems: 'center', gap: 6, height: 46, padding: '0 16px', borderRadius: 999, background: INSET };
const numberInput = { flex: 1, minWidth: 0, width: '100%', height: 40, border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, outline: 'none', boxShadow: 'none' };
const unit = { fontSize: 9, letterSpacing: '.12em', color: SUBTLE, textTransform: 'uppercase' };
const ghostBtn = { height: 28, padding: '0 10px', border: 'none', borderRadius: 999, background: 'transparent', fontFamily: 'inherit', fontSize: 9, letterSpacing: '.1em', color: MUTED, cursor: 'pointer', textTransform: 'uppercase' };

export default function WorkingHoursEditor({ business, onSaved, phone = false, say }) {
  const initial = useMemo(() => fromWindows(business?.availabilityWindows), [business]);
  const [weekly, setWeekly] = useState(initial.weekly);
  // The dated exceptions are edited on the calendar, not here — but they ride every save so
  // SAVE HOURS never wipes a holiday the owner set a minute ago.
  const [exceptions, setExceptions] = useState(initial.exceptions);
  const [noticeHours, setNoticeHours] = useState(business?.minNoticeHours ?? 0);
  // How a booking is actually shaped: how long, how often it can start, and the padding
  // around it. Without these a client can ask for any ragged window inside the hours.
  const [duration, setDuration] = useState(business?.slotDurationMinutes ?? 60);
  const [increment, setIncrement] = useState(business?.slotIncrementMinutes ?? 30);
  const [bufferBefore, setBufferBefore] = useState(business?.bufferBeforeMinutes ?? 0);
  const [bufferAfter, setBufferAfter] = useState(business?.bufferAfterMinutes ?? 0);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    setWeekly(initial.weekly);
    setExceptions(initial.exceptions);
  }, [initial]);

  const setDay = (day, windows) => setWeekly((prev) => prev.map((d) => (d.dayOfWeek === day ? { ...d, windows } : d)));
  const addRange = (day) => {
    const d = weekly.find((x) => x.dayOfWeek === day);
    const last = d.windows[d.windows.length - 1];
    setDay(day, [...d.windows, last ? { startMinute: last.endMinute, endMinute: Math.min(1440, last.endMinute + 120) } : { ...DEFAULT_RANGE }]);
  };
  const editRange = (day, i, field, value) => {
    const d = weekly.find((x) => x.dayOfWeek === day);
    setDay(day, d.windows.map((w, idx) => (idx === i ? { ...w, [field]: toMinutes(value) } : w)));
  };
  const removeRange = (day, i) => {
    const d = weekly.find((x) => x.dayOfWeek === day);
    setDay(day, d.windows.filter((_, idx) => idx !== i));
  };
  /** The bulk move that makes this usable: set one day, apply it everywhere. */
  const copyToWeekdays = (day) => {
    const source = weekly.find((x) => x.dayOfWeek === day)?.windows || [];
    setWeekly((prev) => prev.map((d) => (WEEKDAYS.includes(d.dayOfWeek) ? { ...d, windows: source.map((w) => ({ ...w })) } : d)));
    say?.(`${DAYS[day].toUpperCase()} COPIED TO WEEKDAYS`);
  };
  const copyToAll = (day) => {
    const source = weekly.find((x) => x.dayOfWeek === day)?.windows || [];
    setWeekly((prev) => prev.map((d) => ({ ...d, windows: source.map((w) => ({ ...w })) })));
    say?.(`${DAYS[day].toUpperCase()} COPIED TO EVERY DAY`);
  };

  const save = async () => {
    setSaving(true); setStatus('');
    const r = await proService.setAvailabilityHours({
      weekly: weekly.filter((d) => d.windows.length > 0),
      exceptions,
      minNoticeHours: Number(noticeHours) || 0,
      slotDurationMinutes: Number(duration) || 60,
      slotIncrementMinutes: Number(increment) || 30,
      bufferBeforeMinutes: Number(bufferBefore) || 0,
      bufferAfterMinutes: Number(bufferAfter) || 0,
      timeZoneId: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; } })(),
    });
    setSaving(false);
    setStatus(r?.success ? 'Saved.' : (r?.error || 'Could not save your hours.'));
    if (r?.success) { say?.('HOURS SAVED'); onSaved?.(); }
  };

  const numberField = (label, value, onChange, aria, unitText, extra = {}) => (
    <label className="flex min-w-0 flex-col" style={{ gap: 8 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={numberWrap}>
        <input type="number" min="0" step="5" aria-label={aria} value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" style={numberInput} {...extra} />
        <span style={unit}>{unitText}</span>
      </span>
    </label>
  );

  return (
    <>
      <Card phone={phone} style={{ gap: 12 }}>
        <CardHead title={<span style={{ textTransform: 'uppercase' }}>Hours you work</span>} right="EVERY WEEK UNLESS A DATE SAYS OTHERWISE" />
        <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))', gap: 8 }}>
          {weekly.map((d) => (
            <div key={d.dayOfWeek} className="flex flex-col" style={{ gap: 8, padding: '12px 14px', borderRadius: 18, background: INSET }}>
              <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{DAYS[d.dayOfWeek]}</span>
                {d.windows.length > 0 && (
                  <>
                    <button type="button" onClick={() => copyToWeekdays(d.dayOfWeek)} className="font-mono hover:text-black" style={ghostBtn}>Copy to weekdays</button>
                    <button type="button" onClick={() => copyToAll(d.dayOfWeek)} className="font-mono hover:text-black" style={ghostBtn}>Copy to all</button>
                  </>
                )}
                <button type="button" onClick={() => addRange(d.dayOfWeek)} className="font-mono hover:bg-[#FF00AA]" style={{ height: 28, padding: '0 12px', border: 'none', borderRadius: 999, background: INK, color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color .2s' }}>+ HOURS</button>
              </div>
              {d.windows.length === 0 ? (
                <p className="m-0" style={{ fontSize: 10, letterSpacing: '.12em', color: SUBTLE, textTransform: 'uppercase' }}>Closed</p>
              ) : d.windows.map((w, i) => (
                <span key={i} className="flex items-center" style={{ gap: 6 }}>
                  <input type="time" value={toTime(w.startMinute)} onChange={(e) => editRange(d.dayOfWeek, i, 'startMinute', e.target.value)} aria-label="From" className="pr-pill-input font-mono" style={timePill} />
                  <span style={{ fontSize: 10, color: SUBTLE }}>to</span>
                  <input type="time" value={toTime(w.endMinute)} onChange={(e) => editRange(d.dayOfWeek, i, 'endMinute', e.target.value)} aria-label="To" className="pr-pill-input font-mono" style={timePill} />
                  <RoundX size={30} bg="#fff" aria-label="Remove hours" onClick={() => removeRange(d.dayOfWeek, i)} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </Card>

      <Card phone={phone} style={{ gap: 14 }}>
        <CardHead title="HOW LONG IS A BOOKING" />
        <div className="grid" style={{ gridTemplateColumns: phone ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))', gap: 10 }}>
          {numberField('LENGTH', duration, setDuration, 'Booking length in minutes', 'MIN', { min: 5, max: 1440 })}
          {numberField('STARTS EVERY', increment, setIncrement, 'How often a booking may start, in minutes', 'MIN', { min: 5, max: 1440 })}
          {numberField('BUFFER BEFORE', bufferBefore, setBufferBefore, 'Buffer before, in minutes', 'MIN', { max: 480 })}
          {numberField('BUFFER AFTER', bufferAfter, setBufferAfter, 'Buffer after, in minutes', 'MIN', { max: 480 })}
        </div>
        <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>
          Clients pick from times built out of this — a {duration}-minute booking offered every {increment} minutes.
          {(Number(bufferBefore) > 0 || Number(bufferAfter) > 0) && ' Buffers stay clear either side, so a gap that only fits the job with no room to arrive is never offered.'}
        </span>
        <div className="flex flex-wrap items-end" style={{ gap: 14, paddingTop: 14, borderTop: RULE }}>
          <div style={{ width: phone ? '100%' : 200 }}>{numberField('ADVANCE NOTICE', noticeHours, setNoticeHours, 'Advance notice in hours', 'HOURS', { step: 1, max: 2160 })}</div>
          <span style={{ flex: '1 1 220px', paddingBottom: 12, fontSize: 12, lineHeight: 1.6, color: MUTED }}>
            Nobody can request a slot sooner than this. 0 means same-day is fine.
            {Number(noticeHours) >= 24 && ` That's about ${Math.round(Number(noticeHours) / 24)} day(s).`}
          </span>
        </div>
        <div className="flex flex-wrap items-center" style={{ gap: 12, paddingTop: 14, borderTop: RULE }}>
          <PillButton size={44} onClick={save} disabled={saving} style={{ textTransform: 'uppercase' }}>{saving ? 'Saving…' : 'Save hours'}</PillButton>
          <span style={{ fontSize: 10, letterSpacing: '.12em', color: MUTED, textTransform: 'uppercase' }}>{status || 'Hours save on their own — they don’t wait for the page.'}</span>
        </div>
      </Card>
    </>
  );
}
